import { WORKSPACE_BORDER_RADIUS, WORKSPACE_PADDING } from '@components/v2/constants';
import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Theme, alpha } from '@mui/material';
import { Share } from 'lucide-react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';

const SURFACE_BLUR_IN_PIXELS = 3.3;
const TOP_GLOW_OPACITY = 0.48;
const BOTTOM_GLOW_OPACITY = 0.64;

const getGlowBackground = (theme: Theme) => {
  const glow = theme.palette.common.white;

  return [
    `linear-gradient(183deg, ${alpha(glow, TOP_GLOW_OPACITY)} 21%, ${alpha(glow, 0)} 84%)`,
    `linear-gradient(174deg, ${alpha(glow, 0)} 6%, ${alpha(glow, BOTTOM_GLOW_OPACITY)} 95%)`,
  ].join(', ');
};

interface RachelDropOverlayViewProps {
  /** False when the surrounding surface already draws the workspace frame. */
  hasOwnBorder: boolean;
  /** True when the surrounding surface adds inline padding the overlay has to reach past. */
  hasInlineBleed: boolean;
}

const RachelDropOverlayView = ({ hasOwnBorder, hasInlineBleed }: RachelDropOverlayViewProps) => {
  const { translate } = useTranslation();

  return (
    <Box
      sx={(theme) => ({
        position: 'absolute',
        insetBlock: 0,
        insetInline: hasInlineBleed ? theme.spacing(-WORKSPACE_PADDING) : 0,
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        borderRadius: `${WORKSPACE_BORDER_RADIUS}px`,
        border: hasOwnBorder ? `0.5px solid ${theme.palette.custom.border.primary}` : undefined,
        backgroundColor: theme.palette.custom.backdrop,
        backgroundImage: getGlowBackground(theme),
        backdropFilter: `blur(${SURFACE_BLUR_IN_PIXELS}px)`,
        color: theme.palette.text.primary,
        pointerEvents: 'none',
      })}
    >
      <Icon icon={Share} size="lg" />
      <Typography variant="subtitle1" color="inherit" noWrap>
        {translate(LangEnum.RACHEL_DROP_FILE_HERE)}
      </Typography>
    </Box>
  );
};

export default RachelDropOverlayView;
